// Ask AI on Admin: what its four read-only tools count and list, over the data the tabs already
// read (`/api/routing/admin/*`). Pure: each answer names its tab and says a row as the tab shows it
// ("Belk — Owner Julissa, Backup —; confirm pending"), under ~3 KB (`fitList`). None of them writes.

import type { LineSource } from "./deposco";
import type { AssignmentsPayload } from "./assignments";
import {
  assignmentViews,
  codeHolders,
  emailKey,
  noOwnerItems,
  pendingSuggestions,
  shownGroupName,
  unassignedCustomers,
} from "./assignments";
import type { CustomerNameFilters, CustomerNameRow } from "./customer-names";
import { DEFAULT_CUSTOMER_NAME_FILTERS, filterCustomerRows } from "./customer-names";
import type { DivisionRow } from "./divisions";
import type { RoutingRuleFilters, RoutingRuleRow } from "./routing-rules";
import { DEFAULT_ROUTING_RULE_FILTERS, filterRuleRows, inUseText, isRuleGroup } from "./routing-rules";
import { fitList } from "./ask-fit";

const has = (text: string, part: string) => text.toLowerCase().includes(part.trim().toLowerCase());
const limitOf = (n: number | undefined) => Math.max(0, Math.min(25, n ?? 10));
type Source = "all" | LineSource;

// ---------------------------------------------------------------------------------------------
// Routing rules

/** A routing rule row's window, as the tab's Window in use says it. */
export function ruleRowWindow(r: RoutingRuleRow): string {
  if (r.app.noRule) return "Turned off in the app";
  if (r.inUse.rule) return inUseText(r.inUse.rule);
  return r.inUse.mixed ? "Mixed windows (each code its own)" : "No window";
}

export interface RulesQuery {
  /** Window in use, as the tab's filter buttons: none, a2000, app, off. */
  state?: "none" | "a2000" | "app" | "off";
  /** A customer's shown name, original name or code (the tab's search). */
  customer?: string;
  source?: Source;
  /** Only groups (a shown name over two or more codes), or only single customers. */
  group?: boolean;
  /** Only rows with open lines (default true, as the tab). */
  with_open_lines?: boolean;
  open_bdays?: number;
  due_bdays?: number;
  limit?: number;
}

export function queryRoutingRules(rows: readonly RoutingRuleRow[], q: RulesQuery) {
  const kept = filterRuleRows(rows, {
    search: q.customer ?? "",
    source: q.source ?? "all",
    withOpenLines: q.with_open_lines ?? true,
    inUse: q.state ?? "all",
  }).filter(
    (r) =>
      (q.group === undefined || isRuleGroup(r) === q.group) &&
      (q.open_bdays === undefined || r.inUse.rule?.openBdays === q.open_bdays) &&
      (q.due_bdays === undefined || r.inUse.rule?.dueBdays === q.due_bdays),
  );
  return fitList(
    {
      tab: "Admin → Routing rules",
      matched: kept.length,
      open_lines: kept.reduce((n, r) => n + r.openLines, 0),
      rows: kept.slice(0, limitOf(q.limit)).map((r) => {
        const codes = isRuleGroup(r)
          ? `${r.codes.length} codes${r.separate ? `, ${r.separate} kept separate` : ""}`
          : `${r.codes[0]!.source} ${r.codes[0]!.code}`;
        return `${r.shown} (${codes}) — ${ruleRowWindow(r)}; ${r.openLines} open lines`;
      }),
    },
    "rows",
    kept.length,
  );
}

// ---------------------------------------------------------------------------------------------
// Customer assignments

export interface AssignmentsQuery {
  /** Who owns / backs up this customer: its shown name, original name or code. */
  customer?: string;
  /** A person's customers: a part of their name or their email. */
  person?: string;
  /** Customers with open lines and no Owner (the No owner view). */
  no_owner?: boolean;
  /** Assignments needing confirming: a label with no linked code. */
  needs_confirming?: boolean;
  limit?: number;
}

export function queryAssignments(payload: AssignmentsPayload, q: AssignmentsQuery) {
  const { assignments, customers } = payload;
  const views = assignmentViews(assignments, customers);
  const limit = limitOf(q.limit);
  const tab = "Admin → Customer assignments";
  if (q.no_owner) {
    const items = noOwnerItems(unassignedCustomers(customers, assignments));
    return fitList(
      {
        tab,
        view: "No owner",
        matched: items.length,
        open_lines: items.reduce((n, i) => n + i.lines, 0),
        rows: items
          .slice(0, limit)
          .map(
            (i) =>
              `${i.shown ?? shownGroupName(i.codes[0]!)} (${i.codes.map((c) => c.code).join(", ")}) — ${i.lines} open lines`,
          ),
      },
      "rows",
      items.length,
    );
  }
  if (q.needs_confirming) {
    const needs = views.filter((v) => v.needs);
    return fitList(
      {
        tab,
        view: "Needs confirming",
        matched: needs.length,
        rows: needs
          .slice(0, limit)
          .map(
            (v) =>
              `${v.row.label} — ${v.row.role} ${v.row.personName}; ${
                v.suggestions.length ? `${v.suggestions.length} suggested codes` : "no suggestions"
              }`,
          ),
      },
      "rows",
      needs.length,
    );
  }
  if (q.person) {
    const p = q.person.trim();
    const mine = views.filter(
      (v) =>
        v.row.retired !== "Yes" &&
        !v.person &&
        (has(v.row.personName, p) || emailKey(v.row.personEmail) === emailKey(p)),
    );
    return fitList(
      {
        tab,
        view: "Assignments",
        people: [...new Set(mine.map((v) => v.row.personName))],
        matched: mine.length,
        rows: mine
          .slice(0, limit)
          .map(
            (v) =>
              `${v.row.label} — ${v.row.role} ${v.row.personName}; ${v.keys.length ? v.keys.join(", ") : "not linked yet"}${v.row.note ? `; note: ${v.row.note}` : ""}`,
          ),
      },
      "rows",
      mine.length,
    );
  }
  // Who looks after a customer: each matching customer's Owners and Backups, or a confirm pending.
  const holders = codeHolders(assignments);
  const pending = pendingSuggestions(views);
  const word = q.customer?.trim() ?? "";
  const found = customers.filter((c) => !word || [c.name, c.code, c.shown ?? ""].some((t) => has(t, word)));
  found.sort((a, b) => b.openLines - a.openLines || a.code.localeCompare(b.code));
  const names = (role: "Owner" | "Backup", key: string) =>
    [...new Set((holders.get(key) ?? []).filter((h) => h.role === role).map((h) => h.personName))].join(", ") || "—";
  return fitList(
    {
      tab,
      matched: found.length,
      rows: found.slice(0, limit).map((c) => {
        const waiting = pending.get(c.key) ?? [];
        return (
          `${c.shown || c.name} (${c.source} ${c.code}) — Owner ${names("Owner", c.key)}, Backup ${names("Backup", c.key)}` +
          (waiting.length
            ? `; confirm pending (${waiting.map((w) => `${w.label}, ${w.personName}`).join("; ")})`
            : "") +
          `; ${c.openLines} open lines`
        );
      }),
    },
    "rows",
    found.length,
  );
}

// ---------------------------------------------------------------------------------------------
// Customers' shown names

export interface CustomerNamesQuery {
  search?: string;
  source?: Source;
  /** Named in Admin, or showing the source's default. */
  named?: "named" | "default";
  /** Only shown names two or more customers share. */
  shared?: boolean;
  /** Customers with open lines only. */
  with_open_lines?: boolean;
  limit?: number;
}

export function queryCustomerNames(rows: readonly CustomerNameRow[], q: CustomerNamesQuery) {
  const kept = filterCustomerRows(rows, {
    ...DEFAULT_CUSTOMER_NAME_FILTERS,
    activeOnly: false,
    search: q.search ?? "",
    source: q.source ?? "all",
    named: q.named ?? "all",
    withOpenLines: !!q.with_open_lines,
  });
  const by = new Map<string, CustomerNameRow[]>();
  for (const r of kept) by.set(r.shown, [...(by.get(r.shown) ?? []), r]);
  const shared = [...by].filter(([, of]) => of.length > 1);
  const list = q.shared
    ? shared.map(([shown, of]) => `${shown} — ${of.map((r) => `${r.source} ${r.code}`).join(", ")}`)
    : kept.map(
        (r) =>
          `${r.shown} — ${r.source} ${r.code} (${r.name})${r.isDefault ? ", default" : ", named"}; ${r.openLines} open lines`,
      );
  return fitList(
    {
      tab: "Admin → Customers",
      matched: kept.length,
      named: kept.filter((r) => !r.isDefault).length,
      shown_names_shared: shared.length,
      rows: list.slice(0, limitOf(q.limit)),
    },
    "rows",
    list.length,
  );
}

// ---------------------------------------------------------------------------------------------
// Divisions

export interface DivisionsQuery {
  search?: string;
  source?: Source;
  /** Abbreviated in Admin (true), or showing the default (false). */
  abbreviated?: boolean;
  limit?: number;
}

export function queryDivisions(rows: readonly DivisionRow[], q: DivisionsQuery) {
  const kept = rows.filter(
    (r) =>
      (!q.source || q.source === "all" || r.source === q.source) &&
      (q.abbreviated === undefined || q.abbreviated === !r.isDefault) &&
      (!q.search || [r.corp, r.div, r.name, r.abbrev].some((t) => has(t, q.search!))),
  );
  return fitList(
    {
      tab: "Admin → Divisions",
      matched: kept.length,
      abbreviated: kept.filter((r) => !r.isDefault).length,
      rows: kept
        .slice(0, limitOf(q.limit))
        .map(
          (r) =>
            `${r.abbrev} — ${r.name} (${r.corp} · ${r.div})${r.isDefault ? ", default" : `, set (default ${r.defaultAbbrev})`}; ${r.openLines} open lines`,
        ),
    },
    "rows",
    kept.length,
  );
}

// ---------------------------------------------------------------------------------------------
// apply_view: the tab's own search box and filter buttons, nothing else

/** What a tab lets Ask AI set: its filters as the tab keeps them. */
export type AdminView =
  | { tab: "routing-rules"; filters: RoutingRuleFilters }
  | { tab: "customers"; filters: CustomerNameFilters }
  | { tab: "assignments"; view: "needs" | "assignments" | "no-owner"; search: string; person: string }
  | { tab: "divisions"; search: string; source: Source };

export interface AdminViewInput {
  search?: string;
  source?: Source;
  /** Routing rules: All, No window, From A2000, Set in the app, Turned off. */
  state?: "all" | "none" | "a2000" | "app" | "off";
  with_open_lines?: boolean;
  /** Customers: named, default or all. */
  named?: "all" | "named" | "default";
  /** Customer assignments: the view, and a person (email or a part of the name). */
  view?: "needs" | "assignments" | "no-owner";
  person?: string;
  /** Back to the tab's starting filters. */
  reset?: boolean;
}

/**
 * `apply_view` on an Admin tab: only what the input names and the tab has; the rest comes back
 * as not applied. `people` (Customer assignments): email → name, so a person is set by email.
 */
export function applyAdminView(
  current: AdminView,
  input: AdminViewInput,
  people: readonly { email: string; name: string }[] = [],
): { view: AdminView; unapplied: string[] } {
  const unapplied: string[] = [];
  const not = (k: keyof AdminViewInput) => input[k] !== undefined && unapplied.push(`${k}: not a filter of this tab`);
  switch (current.tab) {
    case "routing-rules": {
      not("named");
      not("view");
      not("person");
      const f = input.reset ? DEFAULT_ROUTING_RULE_FILTERS : current.filters;
      return {
        view: {
          tab: current.tab,
          filters: {
            ...f,
            ...(input.search !== undefined ? { search: input.search } : {}),
            ...(input.source ? { source: input.source } : {}),
            ...(input.state ? { inUse: input.state } : {}),
            ...(input.with_open_lines !== undefined ? { withOpenLines: input.with_open_lines } : {}),
          },
        },
        unapplied,
      };
    }
    case "customers": {
      not("state");
      not("view");
      not("person");
      const f = input.reset ? DEFAULT_CUSTOMER_NAME_FILTERS : current.filters;
      return {
        view: {
          tab: current.tab,
          filters: {
            ...f,
            ...(input.search !== undefined ? { search: input.search } : {}),
            ...(input.source ? { source: input.source } : {}),
            ...(input.named ? { named: input.named } : {}),
            ...(input.with_open_lines !== undefined ? { withOpenLines: input.with_open_lines } : {}),
          },
        },
        unapplied,
      };
    }
    case "assignments": {
      not("state");
      not("named");
      not("source");
      not("with_open_lines");
      let person = input.reset ? "" : current.person;
      if (input.person !== undefined) {
        const p = input.person.trim();
        const like = people.filter((x) => emailKey(x.email) === emailKey(p) || has(x.name, p));
        if (!p) person = "";
        else if (like.length === 1) person = emailKey(like[0]!.email);
        else unapplied.push(like.length ? `person "${p}": several people match` : `person "${p}": nobody on this tab`);
      }
      return {
        view: {
          tab: current.tab,
          view: input.view ?? current.view,
          search: input.search ?? (input.reset ? "" : current.search),
          person,
        },
        unapplied,
      };
    }
    case "divisions":
      not("state");
      not("named");
      not("view");
      not("person");
      not("with_open_lines");
      return {
        view: {
          tab: current.tab,
          search: input.search ?? (input.reset ? "" : current.search),
          source: input.source ?? (input.reset ? "all" : current.source),
        },
        unapplied,
      };
  }
}

/** The view as one key: Undo shows while the tab is still the view Ask AI set. */
export const adminViewKey = (v: AdminView) => JSON.stringify(v);

/** Admin's tabs with filters Ask AI may set, and what each calls itself. */
export const ADMIN_ASK_TABS = ["routing-rules", "assignments", "customers", "divisions"] as const;
export type AdminAskTabId = (typeof ADMIN_ASK_TABS)[number];
export const ADMIN_TAB_NAMES: Record<AdminAskTabId, string> = {
  "routing-rules": "Routing rules",
  assignments: "Customer assignments",
  customers: "Customers",
  divisions: "Divisions",
};

/** Which tabs have each `apply_view` filter (the search box: every tab; Reset: the tab it is said on). */
const FILTER_TABS: Record<Exclude<keyof AdminViewInput, "search" | "reset">, readonly AdminAskTabId[]> = {
  source: ["routing-rules", "customers", "divisions"],
  state: ["routing-rules"],
  with_open_lines: ["routing-rules", "customers"],
  named: ["customers"],
  view: ["assignments"],
  person: ["assignments"],
};

/**
 * Where an `apply_view` goes from the tab `on`: here, when this tab has every filter it names (or it
 * names none of a tab's own); `open`, the one other tab that has them all — the bar switches to it and
 * applies them there; else `tabs`, the tabs those filters are on (two or more: the answer offers them).
 */
export function adminViewRoute(
  on: AdminAskTabId,
  input: AdminViewInput,
): { to: "here" } | { to: "open"; tab: AdminAskTabId } | { to: "elsewhere"; tabs: AdminAskTabId[] } {
  const named = (Object.keys(FILTER_TABS) as (keyof typeof FILTER_TABS)[]).filter((k) => input[k] !== undefined);
  const has = (t: AdminAskTabId) => named.every((k) => FILTER_TABS[k].includes(t));
  if (!named.length || has(on)) return { to: "here" };
  const all = ADMIN_ASK_TABS.filter(has);
  if (all.length === 1) return { to: "open", tab: all[0]! };
  // No tab has them all (or several do): the tabs of the filters this one lacks.
  const tabs = [...new Set(named.filter((k) => !FILTER_TABS[k].includes(on)).flatMap((k) => FILTER_TABS[k]))];
  return { to: "elsewhere", tabs: ADMIN_ASK_TABS.filter((t) => tabs.includes(t) && t !== on) };
}
